/**
 * TransferLedger — variance-flagged OUT / IN reconciliation view.
 *
 * Admins pick a warehouse to see everything that shipped out of it +
 * everything that landed in it, with variance badges and settlement
 * status per line. Totals band at the top gives a quick "did we bill
 * everything we should have" health check.
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Receipt, Warning, ArrowsClockwise, Buildings, TrendUp,
} from '@phosphor-icons/react';
import { getWarehouses, getTransferLedger } from '../lib/api';

const STATUS_COLOURS = {
  issued: 'text-amber-800 bg-amber-100 border-amber-300',
  paid: 'text-emerald-800 bg-emerald-100 border-emerald-300',
  unsettled: 'text-rose-800 bg-rose-100 border-rose-300',
};

export default function TransferLedger() {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [ledger, setLedger] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getWarehouses();
        setWarehouses(r.data);
      } catch (e) { toast.error('Could not load warehouses'); }
    })();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await getTransferLedger(warehouseId ? { warehouse_id: warehouseId } : {});
      setLedger(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load ledger');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [warehouseId]);

  const t = ledger.totals || {};

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-4" data-testid="transfer-ledger-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Receipt size={28} weight="fill" className="text-brand" /> Transfer Ledger
            </h1>
            <p className="text-sm text-muted-foreground">
              Every transfer in and out of the selected warehouse, with variance flags and settlement status.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={warehouseId || 'all'} onValueChange={(v) => setWarehouseId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-56" data-testid="ledger-warehouse-select">
                <SelectValue placeholder="All warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All warehouses</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={refresh} data-testid="ledger-refresh-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ledger-kpis">
          <Kpi label="OUT (units)" value={t.out_units || 0} color="emerald" icon={TrendUp} testId="kpi-out-units" />
          <Kpi label="IN (units)" value={t.in_units || 0} color="sky" icon={TrendUp} testId="kpi-in-units" />
          <Kpi label="Variance" value={`${t.variance_units || 0}`} color={t.variance_units ? 'rose' : 'emerald'} icon={Warning} testId="kpi-variance" />
          <Kpi label="Settled" value={`$${(t.settlement_value || 0).toFixed(2)}`} color="violet" icon={Receipt} testId="kpi-settled" />
          <Kpi label="Unsettled" value={`$${(t.unsettled_value || 0).toFixed(2)}`} color={t.unsettled_value > 0 ? 'amber' : 'emerald'} icon={Warning} testId="kpi-unsettled" />
        </div>

        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs" data-testid="ledger-table">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-widest">Transfer #</th>
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-widest">Route</th>
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-widest">Perspective</th>
                <th className="text-right px-2 py-2 text-[10px] uppercase tracking-widest">Shipped</th>
                <th className="text-right px-2 py-2 text-[10px] uppercase tracking-widest">Received</th>
                <th className="text-right px-2 py-2 text-[10px] uppercase tracking-widest">Variance</th>
                <th className="text-right px-2 py-2 text-[10px] uppercase tracking-widest">Goods $</th>
                <th className="text-right px-2 py-2 text-[10px] uppercase tracking-widest">Settlement</th>
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-widest">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">Loading ledger…</td></tr>
              )}
              {!loading && (ledger.rows || []).length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">
                  <Buildings size={24} className="inline mr-2 opacity-50" /> No transfers for this warehouse yet.
                </td></tr>
              )}
              {(ledger.rows || []).map((r) => (
                <tr key={r.transfer_id} className="border-b border-border/50" data-testid={`ledger-row-${r.transfer_id}`}>
                  <td className="px-2 py-1.5 font-mono">
                    <Link to={`/transfers`} className="hover:underline">{r.transfer_number}</Link>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="font-mono font-bold">{r.from_warehouse_code}</span>
                    <span className="text-muted-foreground mx-1">→</span>
                    <span className="font-mono font-bold">{r.to_warehouse_code}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    {r.perspective ? (
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${r.perspective === 'out' ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>
                        {r.perspective}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.shipped_units}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.received_units}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.has_variance ? (
                      <span className="text-rose-700 font-bold" data-testid={`variance-${r.transfer_id}`}>
                        {r.variance_units > 0 ? '+' : ''}{r.variance_units}
                      </span>
                    ) : <span className="text-muted-foreground">0</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">${(r.goods_value || 0).toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.settlement_total ? `$${Number(r.settlement_total).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-2 py-1.5">
                    {r.settlement_status && (
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border ${STATUS_COLOURS[r.settlement_status] || 'bg-muted'}`}>
                        {r.settlement_status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Kpi({ label, value, color = 'sky', icon: Icon, testId }) {
  const cls = {
    sky: 'text-sky-700 bg-sky-50 border-sky-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    rose: 'text-rose-700 bg-rose-50 border-rose-200',
    violet: 'text-violet-700 bg-violet-50 border-violet-200',
  }[color];
  return (
    <div data-testid={testId} className={`border ${cls} p-3`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold opacity-80">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className="text-2xl font-bold font-mono mt-1">{value}</div>
    </div>
  );
}
